import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  EstadoPedidoMaterial,
  IMaterialServicioTecnico,
  IPopulate,
  IQueryParam,
  IValoresEventoTecnico,
} from 'modelos/src';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import { IFiltroTabla } from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { MaterialesServicioTecnicoService } from '../../../../auxiliares/servicios/http/materiales-servicio-tecnico.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { EventosGenericosHttpService } from '../../../../auxiliares/servicios/http/eventos-genericos.service';
import { DetallesEventoGenericoComponent } from '../../eventos-genericos/detalles-evento-generico/detalles-evento-generico.component';
import { WebSocketService } from '../../../../auxiliares/servicios/websocket.service';
import { ReconciliacionWs } from '../../../../auxiliares/servicios/ws-reconciliacion';
import { LoginService } from '../../../login/login.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { IEstadoCelda } from '../../../../auxiliares/tabla/tabla.interfaces';

/** Estado del material (pieza `estado`): ícono de docs/ICONOS.md §4 + tono. */
const ESTADOS_MATERIAL: Record<EstadoPedidoMaterial, IEstadoCelda> = {
  Pendiente: { texto: 'Pendiente', tono: 'aviso', icono: 'pending' },
  Aprobado: { texto: 'Aprobado', tono: 'info', icono: 'check_circle' },
  Asignado: { texto: 'Asignado', tono: 'ok', icono: 'check_circle' },
  Usado: { texto: 'Usado', tono: 'ok', icono: 'task_alt' },
  Anulado: { texto: 'Anulado', tono: 'neutro', icono: 'block' },
  Devuelto: { texto: 'Devuelto', tono: 'neutro' },
};

/** Tipo de movimiento: no es un estado, va con tono informativo o neutro. */
const TIPOS_MATERIAL: Record<string, IEstadoCelda> = {
  pedido: { texto: 'Pedido', tono: 'info' },
  asignado: { texto: 'Asignado', tono: 'neutro' },
  usado: { texto: 'Usado', tono: 'neutro' },
};

@Component({
  selector: 'app-pedidos-materiales',
  templateUrl: './pedidos-materiales.component.html',
  styleUrl: './pedidos-materiales.component.scss',
  standalone: false,
})
export class PedidosMaterialesComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private service = inject(MaterialesServicioTecnicoService);
  private lista2 = inject(Lista2Service);
  private eventosService = inject(EventosGenericosHttpService);
  private dialogService = inject(DialogService);
  private dialog = inject(MatDialog);
  private wsService = inject(WebSocketService);

  private reconciliacionWs = new ReconciliacionWs(this.wsService, {
    entidades: ['materialesserviciotecnico'],
    idPermiso: () => LoginService.getIdPermiso(),
    relistar: () => this.relistarTodo(),
  });

  public name = 'PedidosMaterialesComponent';
  public tipoCliente = HelperService.getTipoCliente();

  public selectedTab = 0;
  public columnas?: IColumnas<IMaterialServicioTecnico>[];

  // Filtro base de cada solapa. Pendientes = pedidos por aprobar; Historial = el resto.
  private readonly FILTRO_PENDIENTES = { tipo: 'pedido', estado: 'Pendiente' };
  private readonly FILTRO_HISTORIAL = {
    $nor: [{ tipo: 'pedido', estado: 'Pendiente' }],
  };

  // Pendientes de aprobación (solapa + badge)
  public datosPendientes: IMaterialServicioTecnico[] = [];
  public totalPendientes = 0;
  private pendientes$?: Subscription;

  // Historial (usados, asignados, devueltos, aprobados, anulados…)
  public datosHistorial: IMaterialServicioTecnico[] = [];
  public totalHistorial = 0;
  private historial$?: Subscription;

  // Visor de fotos
  public viewerImagenes: string[] = [];
  public viewerIndex = 0;
  public viewerVisible = false;

  private populate: IPopulate[] = [
    // idEntidad: de ahí se populan las entidades del ST (columna Entidad).
    {
      path: 'evento',
      select: 'valores tipoEvento estado idEntidad',
      populate: [
        { path: 'alarma', select: 'nombre' },
        { path: 'luminaria', select: 'identificacion' },
        { path: 'tracker', select: 'nombre identificacion' },
        { path: 'activo', select: 'identificacion categoria' },
        { path: 'sirena', select: 'direccion chipId' },
        { path: 'gateway', select: 'nombre' },
      ],
    },
    { path: 'usuario', select: 'usuario datosPersonales' },
    { path: 'cliente', select: 'nombre' },
  ];

  private baseQuery(): IQueryParam {
    return {
      populate: JSON.stringify(this.populate),
      sort: '-fechaCreacion',
      page: 0,
      limit: +this.helper.getPageSize(this.name),
      includeChildren: true,
      childrenLevel: 0,
    };
  }

  public queryPendientes: IQueryParam = this.baseQuery();
  public queryHistorial: IQueryParam = this.baseQuery();

  // Filtros
  private tipoFiltro: IFiltroTabla = {
    elementos: [
      { label: 'Usado', value: 'usado' },
      { label: 'Pedido', value: 'pedido' },
      { label: 'Asignado', value: 'asignado' },
    ],
    filter: {
      field: 'tipo',
    },
    label: 'Tipo',
    tipo: 'select',
    selectLabel: 'label',
    selectValue: 'value',
  };
  private estadoFiltro: IFiltroTabla = {
    elementos: [
      'Pendiente',
      'Aprobado',
      'Usado',
      'Anulado',
      'Asignado',
      'Devuelto',
    ],
    filter: {
      field: 'estado',
    },
    label: 'Estado',
    rapido: true,
    tipo: 'select',
  };
  private fechaFiltro: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Fecha',
    tipo: 'dateRange',
  };
  private clienteFiltro: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  private ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  // Historial: todos los filtros. Pendientes: tipo/estado ya fijos, solo fecha/cliente.
  public filtrosHistorial: IFiltroTabla[] = [
    this.tipoFiltro,
    this.estadoFiltro,
    this.fechaFiltro,
    this.clienteFiltro,
    this.ancestro,
  ];
  public filtrosPendientes: IFiltroTabla[] = [
    this.fechaFiltro,
    this.clienteFiltro,
    this.ancestro,
  ];

  // Combina el filtro base de la solapa con los filtros elegidos por el usuario
  // (sin mutar el query bindeado a la tabla, para no disparar loops de queryChange).
  private conBase(
    query: IQueryParam,
    base: Record<string, unknown>,
  ): IQueryParam {
    const userFilter = query.filter ? JSON.parse(query.filter) : {};
    const filter = Object.keys(userFilter).length
      ? { $and: [base, userFilter] }
      : base;
    return { ...query, filter: JSON.stringify(filter) };
  }

  public listarPendientes(): void {
    this.pendientes$?.unsubscribe();
    const q = this.conBase(this.queryPendientes, this.FILTRO_PENDIENTES);
    this.pendientes$ = this.lista2
      .materialesServicioTecnico(q, this.reconciliacionWs.intervaloActual())
      .subscribe((res) => {
        this.datosPendientes = res.datos;
        this.totalPendientes = res.totalCount;
      });
  }

  public listarHistorial(): void {
    this.historial$?.unsubscribe();
    const q = this.conBase(this.queryHistorial, this.FILTRO_HISTORIAL);
    this.historial$ = this.lista2
      .materialesServicioTecnico(q, this.reconciliacionWs.intervaloActual())
      .subscribe((res) => {
        this.datosHistorial = res.datos;
        this.totalHistorial = res.totalCount;
      });
  }

  private relistarTodo(): void {
    this.listarPendientes();
    this.listarHistorial();
  }

  public async verDetallesST(dato: IMaterialServicioTecnico): Promise<void> {
    if (!dato.idEventoGenerico) {
      await this.dialogService.dialog(
        'Sin servicio técnico',
        'Este material no tiene un servicio técnico asociado.',
        'Cerrar',
      );
      return;
    }
    try {
      const evento = await this.eventosService.getById(dato.idEventoGenerico);
      this.dialog.open(DetallesEventoGenericoComponent, {
        data: { evento },
        width: '900px',
        maxWidth: '95vw',
      });
    } catch (error) {
      console.error(error);
      this.helper.notifError('No se pudo cargar el servicio técnico');
    }
  }

  public verFotos(dato: IMaterialServicioTecnico): void {
    if (!dato.imagenes?.length) return;
    this.viewerImagenes = dato.imagenes;
    this.viewerIndex = 0;
    this.viewerVisible = true;
  }

  public async cambiarEstado(
    dato: IMaterialServicioTecnico,
    estado: EstadoPedidoMaterial,
    titulo: string,
    mensaje: string,
  ): Promise<void> {
    const res = await this.dialogService.confirm(titulo, mensaje);
    if (!res) return;
    try {
      await this.service.update(dato._id!, { estado });
      this.helper.notifSuccess(`Material: ${estado.toLowerCase()}`);
      this.relistarTodo();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  private puedeGestionar(): boolean {
    return this.helper.tienePermiso(
      'Servicio Técnico - Gestionar pedidos de materiales',
    );
  }

  // El servicio técnico del material ya terminó: no tiene sentido gestionar
  // (aprobar / usar / anular) sus materiales.
  private stFinalizado(dato: IMaterialServicioTecnico): boolean {
    return dato.evento?.estado === 'Finalizado';
  }

  // [ícono, nombre] de la entidad del servicio técnico del material.
  private entidadDelServicio(dato: IMaterialServicioTecnico): [string, string] {
    const ev = dato.evento;
    if (ev?.alarma?.nombre) return ['doorbell', ev.alarma.nombre];
    if (ev?.luminaria?.identificacion)
      return ['lightbulb', ev.luminaria.identificacion];
    if (ev?.tracker?.nombre || ev?.tracker?.identificacion)
      return ['gps_fixed', ev.tracker.nombre || ev.tracker.identificacion!];
    if (ev?.sirena)
      return [
        'campaign',
        ev.sirena.direccion || ev.sirena.chipId || ev.sirena._id!,
      ];
    if (ev?.gateway?.nombre) return ['podcasts', ev.gateway.nombre];
    if (ev?.activo?.identificacion)
      return ['directions_car', ev.activo.identificacion];
    return ['', ''];
  }

  private setColumnas() {
    const columnas: IColumnas<IMaterialServicioTecnico>[] = [
      // Fecha
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          hace: true,
          noData: 'Sin fecha',
        },
      },
      // Código de incidente del ST
      {
        header: {
          label: 'Servicio técnico',
        },
        row: {
          parse(dato) {
            const numeroIncidente = (
              dato.evento?.valores as IValoresEventoTecnico
            )?.numeroIncidente;
            return numeroIncidente?.toUpperCase();
          },
          noData: 'Sin código',
        },
      },
      // Entidad del ST (para qué es el material). Mismo formato que el
      // listado de servicios técnicos. El stock asignado no tiene ST.
      {
        header: {
          label: 'Entidad',
        },
        row: {
          html: (dato) => {
            const [icono, nombre] = this.entidadDelServicio(dato);
            if (!nombre) return '';
            return `<div class="tabla-linea"><span class="material-symbols-outlined">${icono}</span><span>${escaparHtml(nombre)}</span></div>`;
          },
          noData: 'Sin entidad',
        },
      },
      // Solicitante
      {
        header: {
          label: 'Solicitante',
        },
        row: {
          parse(dato) {
            return (
              dato.usuario?.datosPersonales?.nombre || dato.usuario?.usuario
            );
          },
          noData: 'Sin solicitante',
        },
      },
      // Material — misma estética que la columna Materiales del listado de técnicos.
      {
        header: {
          label: 'Material',
        },
        row: {
          html: (dato) => {
            const nombre = dato.descripcion || dato.idEntidad;
            if (!nombre) {
              return `<span class="no-info">Sin descripción</span>`;
            }
            const cantidad = (dato.cantidad ?? 1).toLocaleString('es-AR');
            return `<div class="tabla-celda-fila"><span>${escaparHtml(nombre)}</span><strong>×${escaparHtml(cantidad)}</strong></div>`;
          },
        },
      },
      // Tipo
      {
        header: {
          label: 'Tipo',
          sortable: true,
          sort: 'tipo',
        },
        row: {
          estado: (dato) =>
            TIPOS_MATERIAL[dato.tipo ?? 'usado'] ?? TIPOS_MATERIAL['usado'],
        },
      },
      // Estado
      {
        header: {
          label: 'Estado',
          sortable: true,
          sort: 'estado',
        },
        row: {
          estado: (dato) =>
            dato.estado
              ? (ESTADOS_MATERIAL[dato.estado] ?? {
                  texto: dato.estado,
                  tono: 'neutro',
                })
              : undefined,
          noData: 'Sin estado',
        },
      },
    ];

    // Cliente
    if (this.tipoCliente !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver servicio técnico',
            click: (dato) => this.verDetallesST(dato),
          },
          {
            tipo: 'material',
            icon: 'photo_library',
            tooltip: 'Ver fotos',
            oculta: (dato) => !dato.imagenes?.length,
            click: (dato) => this.verFotos(dato),
          },
          {
            tipo: 'material',
            icon: 'thumb_up',
            tooltip: 'Aprobar pedido',
            oculta: (dato) =>
              !this.puedeGestionar() ||
              this.stFinalizado(dato) ||
              dato.tipo !== 'pedido' ||
              dato.estado !== 'Pendiente',
            click: (dato) =>
              this.cambiarEstado(
                dato,
                'Aprobado',
                'Aprobar pedido',
                '¿Querés aprobar el pedido? El técnico deberá volver a terminar el trabajo con el material.',
              ),
          },
          {
            tipo: 'material',
            icon: 'check_circle',
            tooltip: 'Marcar como usado',
            // Pedido aprobado, o material asignado (stock del técnico) en su caja.
            oculta: (dato) =>
              !this.puedeGestionar() ||
              this.stFinalizado(dato) ||
              !(
                (dato.tipo === 'pedido' && dato.estado === 'Aprobado') ||
                (dato.tipo === 'asignado' && dato.estado === 'Asignado')
              ),
            click: (dato) =>
              this.cambiarEstado(
                dato,
                'Usado',
                'Marcar como usado',
                '¿Querés marcar el material como usado?',
              ),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'block',
            tooltip: 'Anular',
            // Pedido no cerrado, o material asignado en la caja del técnico
            // (anular lo saca del stock y libera el dispositivo). Con el ST
            // finalizado solo se puede anular un pedido que quedó Pendiente
            // (los anteriores al anulado automático al finalizar).
            oculta: (dato) =>
              !this.puedeGestionar() ||
              (this.stFinalizado(dato) &&
                !(dato.tipo === 'pedido' && dato.estado === 'Pendiente')) ||
              !(
                (dato.tipo === 'pedido' &&
                  dato.estado !== 'Usado' &&
                  dato.estado !== 'Anulado') ||
                (dato.tipo === 'asignado' && dato.estado === 'Asignado')
              ),
            click: (dato) =>
              this.cambiarEstado(
                dato,
                'Anulado',
                'Anular pedido',
                '¿Querés anular el pedido de material?',
              ),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  async ngOnInit() {
    this.setColumnas();
    this.reconciliacionWs.iniciar();
    // Ambas solapas activas: la de pendientes mantiene el badge al día aunque
    // se esté viendo el historial.
    this.relistarTodo();
  }

  ngOnDestroy() {
    this.reconciliacionWs.detener();
    this.pendientes$?.unsubscribe();
    this.historial$?.unsubscribe();
  }
}
